/* YallaDJ v3 — global preview-player engine (Store PR 5).
 * Promotes the slice-013 preview engine (was assets/preview-player.js) to a
 * site-wide floating bar. Faithful port of src/lib/player-store.ts +
 * GlobalPlayer.tsx, adapted to a full-page-reload Liquid storefront.
 *
 * One shared <audio>; controls exist ONLY where a row rendered a
 * [data-preview-play] button (public yalladj.sample_url present). Single
 * concurrent playback, 30s hard cap, click/keyboard seek, no autoplay,
 * graceful reset on error/end. Resumes the current track across navigations
 * via sessionStorage (best-effort: falls back to paused-at-position when the
 * browser blocks autoplay after a navigation). Only ever plays the public 30s
 * sample URL carried in the button's data attribute — never full files,
 * private keys, or signed URLs; reads NO metafields. Drives the bar markup in
 * snippets/global-player.liquid, mounted once in layout/theme.liquid so it
 * persists on every template. RTL-aware; honours prefers-reduced-motion. */
(function () {
  'use strict';

  var SAMPLE_SECONDS = 30; // hard cap — mirrors the app player + inline sample player
  var STORE_KEY = 'yalladj:gp'; // sessionStorage resume key (per-tab, ephemeral)
  var SAVE_THROTTLE_MS = 1000;

  var bar = null;
  var els = {};
  var audio = null;
  var currentUrl = null; // identity = the sample URL (handles duplicate card+row buttons)
  var lastSave = 0;
  var booted = false;
  var state = { playing: false, loading: false, error: false };

  function clamp(n, lo, hi) { return n < lo ? lo : (n > hi ? hi : n); }

  function mmss(s) {
    s = Math.max(0, Math.floor(s));
    return Math.floor(s / 60) + ':' + ('0' + (s % 60)).slice(-2);
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function isRtl() {
    return (document.documentElement.getAttribute('dir') || '') === 'rtl';
  }

  /* ---------- sessionStorage resume ---------- */
  function save(force) {
    if (!currentUrl) return;
    var now = Date.now();
    if (!force && now - lastSave < SAVE_THROTTLE_MS) return;
    lastSave = now;
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify({
        url: currentUrl,
        title: els.title ? els.title.textContent : '',
        artist: els.artist ? els.artist.textContent : '',
        href: els.link ? (els.link.getAttribute('href') || '') : '',
        t: audio ? audio.currentTime : 0,
        playing: state.playing
      }));
    } catch (e) { /* storage unavailable / full — ignore */ }
  }

  function clearSaved() {
    try { sessionStorage.removeItem(STORE_KEY); } catch (e) { /* */ }
  }

  function readSaved() {
    try {
      var raw = sessionStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      return (data && data.url) ? data : null;
    } catch (e) { return null; }
  }

  /* ---------- audio element ---------- */
  function ensureAudio() {
    if (audio) return audio;
    audio = new Audio();
    audio.preload = 'none';
    audio.addEventListener('playing', function () { set({ loading: false, playing: true, error: false }); });
    audio.addEventListener('pause', function () {
      if (!audio || audio.ended) return;
      set({ playing: false });
      // Persist only a genuine user pause. Skip the spurious pause events fired by
      // the 30s cap / onEnded (currentTime forced to 0) and by a track switch
      // (loading), so clearSaved() stays authoritative and resume() cannot revive
      // a finished or just-replaced track.
      if (!state.loading && audio.currentTime > 0) save(true);
    });
    audio.addEventListener('waiting', function () { set({ loading: true }); });
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', function () {
      if (!currentUrl) return; // ignore the error fired when close() clears the src
      set({ loading: false, playing: false, error: true });
    });
    audio.addEventListener('timeupdate', onTime);
    return audio;
  }

  function onEnded() {
    if (audio) audio.currentTime = 0;
    set({ playing: false });
    renderProgress(0);
    clearSaved();
  }

  function onTime() {
    if (!audio) return;
    var t = audio.currentTime;
    if (t >= SAMPLE_SECONDS) { // 30s hard cap
      audio.pause();
      audio.currentTime = 0;
      set({ playing: false });
      renderProgress(0);
      clearSaved();
      return;
    }
    renderProgress(t);
    save(false);
  }

  /* ---------- state -> DOM ---------- */
  function set(partial) {
    for (var k in partial) { if (Object.prototype.hasOwnProperty.call(partial, k)) state[k] = partial[k]; }
    apply();
  }

  function apply() {
    if (!bar) return;
    bar.classList.toggle('is-playing', state.playing && !state.loading);
    if (els.toggle) {
      els.toggle.classList.toggle('is-playing', state.playing && !state.loading);
      els.toggle.classList.toggle('is-loading', state.loading);
      els.toggle.setAttribute('aria-label', state.playing
        ? (els.toggle.getAttribute('data-label-pause') || '')
        : (els.toggle.getAttribute('data-label-play') || ''));
    }
    // .animate-spin-slow is already gated behind prefers-reduced-motion in design-tokens.css
    if (els.vinyl) els.vinyl.classList.toggle('animate-spin-slow', state.playing);
    if (els.error) els.error.hidden = !state.error;
    setPlayButtons(currentUrl, state.playing);
  }

  function renderProgress(t) {
    var pct = clamp((t / SAMPLE_SECONDS) * 100, 0, 100);
    if (els.fill) els.fill.style.inlineSize = pct + '%';
    if (els.dot) els.dot.style.insetInlineStart = pct + '%';
    if (els.time) els.time.textContent = mmss(Math.min(t, SAMPLE_SECONDS)) + ' / ' + mmss(SAMPLE_SECONDS);
    if (els.seek) els.seek.setAttribute('aria-valuenow', String(Math.min(Math.floor(t), SAMPLE_SECONDS)));
  }

  // Reflect the active-track state on every matching row button (card + desktop twins).
  function setPlayButtons(url, playing) {
    var btns = document.querySelectorAll('[data-preview-play]');
    for (var i = 0; i < btns.length; i++) {
      var match = !!url && btns[i].getAttribute('data-preview-url') === url;
      btns[i].classList.toggle('is-playing', match && playing);
      btns[i].setAttribute('aria-pressed', match && playing ? 'true' : 'false');
    }
  }

  function showBar() {
    if (!bar || !bar.hidden) return;
    bar.hidden = false;
    if (!reducedMotion()) {
      bar.classList.remove('animate-slide-up');
      void bar.offsetWidth; // reflow so the entrance re-runs on each fresh reveal
      bar.classList.add('animate-slide-up');
    }
  }

  function hideBar() {
    if (!bar) return;
    bar.hidden = true;
    bar.classList.remove('animate-slide-up');
  }

  function populate(meta) {
    if (els.title) els.title.textContent = meta.title || '';
    if (els.artist) {
      els.artist.textContent = meta.artist || '';
      els.artist.hidden = !meta.artist;
    }
    if (els.link) {
      if (meta.href) els.link.setAttribute('href', meta.href);
      else els.link.removeAttribute('href');
    }
  }

  // Only ever feed an http(s) URL to the media element. The public sample_url is
  // always an absolute R2 https URL; this rejects a malformed/relative metafield
  // (or a javascript:/data:/blob: value) at the boundary — defense in depth, and
  // it turns a bad value into a clean no-op instead of a media-error badge. Note
  // javascript:/data: on a media src never execute (that needs a navigational
  // sink); this is hygiene at the input edge, not a fix for a live exploit.
  function isSafeUrl(u) {
    if (!u) return false;
    try {
      var proto = new URL(u, document.baseURI).protocol;
      return proto === 'https:' || proto === 'http:';
    } catch (e) {
      return false;
    }
  }

  /* ---------- transport ---------- */
  function playUrl(url, meta) {
    ensureAudio();
    currentUrl = url;
    populate(meta);
    renderProgress(0);
    set({ loading: true, error: false, playing: false });
    showBar();
    audio.src = url;
    audio.currentTime = 0;
    var p = audio.play();
    if (p && p.catch) p.catch(function () { set({ loading: false, playing: false, error: true }); });
  }

  function toggle() {
    if (!audio || !currentUrl) return;
    if (state.playing) {
      audio.pause();
    } else {
      var p = audio.play();
      if (p && p.catch) p.catch(function () { set({ loading: false, error: true }); });
    }
  }

  function seekRatio(ratio) {
    if (!audio) return;
    var max = SAMPLE_SECONDS;
    if (audio.duration && isFinite(audio.duration)) max = Math.min(SAMPLE_SECONDS, audio.duration);
    var t = clamp(ratio, 0, 1) * max;
    audio.currentTime = t;
    renderProgress(t);
    save(true);
  }

  function closePlayer() {
    if (audio) {
      try { audio.pause(); } catch (e) { /* */ }
      audio.src = '';
    }
    set({ playing: false, loading: false, error: false });
    setPlayButtons(currentUrl, false);
    currentUrl = null;
    clearSaved();
    hideBar();
  }

  function onPreviewClick(btn) {
    var url = btn.getAttribute('data-preview-url');
    if (!isSafeUrl(url)) return;
    if (url === currentUrl && audio) { toggle(); return; }
    playUrl(url, {
      title: btn.getAttribute('data-preview-title') || '',
      artist: btn.getAttribute('data-preview-artist') || '',
      href: btn.getAttribute('data-preview-href') || ''
    });
  }

  /* ---------- resume across full-page navigations ---------- */
  function resume() {
    var data = readSaved();
    if (!data) return;
    // Purge a poisoned/stale entry (e.g. a non-http url) rather than acting on it.
    if (!isSafeUrl(data.url)) { clearSaved(); return; }
    var startAt = clamp(Number(data.t) || 0, 0, SAMPLE_SECONDS);
    // A track saved at (or a hair under) the 30s cap is effectively finished —
    // restoring it would just snap back to 0:00, so drop it instead of reviving.
    if (startAt >= SAMPLE_SECONDS - 0.5) { clearSaved(); return; }
    ensureAudio();
    currentUrl = data.url;
    populate({ title: data.title, artist: data.artist, href: data.href });
    showBar();
    renderProgress(startAt);
    var seekOnce = function () {
      audio.removeEventListener('loadedmetadata', seekOnce);
      audio.removeEventListener('error', onResumeFail);
      // A different track (or a close) superseded this resume before its metadata
      // loaded — abandon the stale seek so we never seek/play the wrong track.
      if (currentUrl !== data.url) return;
      try { audio.currentTime = startAt; } catch (e) { /* */ }
      if (data.playing) {
        // Best-effort: autoplay after a navigation is usually blocked, so this
        // commonly resolves to paused-at-position. Play AFTER the seek so resume
        // never emits a 0:00 burst when autoplay IS granted.
        var p = audio.play();
        if (p && p.catch) p.catch(function () { set({ loading: false, playing: false }); });
      }
    };
    // If the saved sample is gone (unpublished / deleted / 404), drop the dead
    // entry and tear the bar down rather than re-showing it on every page.
    var onResumeFail = function () {
      audio.removeEventListener('loadedmetadata', seekOnce);
      audio.removeEventListener('error', onResumeFail);
      // Superseded by a new track / close — let that track's own handlers run.
      if (currentUrl !== data.url) return;
      closePlayer();
    };
    audio.addEventListener('loadedmetadata', seekOnce);
    audio.addEventListener('error', onResumeFail);
    // 'metadata' (not 'none') so loadedmetadata fires and the seek applies even
    // for a paused resume — the next play() then starts at the saved position.
    audio.preload = 'metadata';
    audio.src = data.url;
    set({ loading: !!data.playing, playing: false, error: false });
  }

  /* ---------- boot ---------- */
  function cacheEls() {
    bar = document.querySelector('[data-global-player]');
    if (!bar) return false;
    els.title = bar.querySelector('[data-gp-title]');
    els.artist = bar.querySelector('[data-gp-artist]');
    els.link = bar.querySelector('[data-gp-link]');
    els.time = bar.querySelector('[data-gp-time]');
    els.fill = bar.querySelector('[data-gp-fill]');
    els.dot = bar.querySelector('[data-gp-dot]');
    els.vinyl = bar.querySelector('[data-gp-vinyl]');
    els.toggle = bar.querySelector('[data-gp-toggle]');
    els.close = bar.querySelector('[data-gp-close]');
    els.error = bar.querySelector('[data-gp-error]');
    els.seek = bar.querySelector('[data-gp-seek]');
    return true;
  }

  function onSeekClick(e) {
    var rect = els.seek.getBoundingClientRect();
    if (!rect.width) return;
    var ratio = (e.clientX - rect.left) / rect.width;
    if (isRtl()) ratio = 1 - ratio;
    seekRatio(ratio);
  }

  function onSeekKey(e) {
    // Arrow keys map to time direction (Right = forward) on both LTR and RTL —
    // the standard media-player convention. This is intentionally NOT visually
    // mirrored like the click handler (which maps screen position to time).
    var cur = audio ? audio.currentTime : 0;
    if (e.key === 'ArrowRight') { seekRatio((cur + 1) / SAMPLE_SECONDS); e.preventDefault(); }
    else if (e.key === 'ArrowLeft') { seekRatio((cur - 1) / SAMPLE_SECONDS); e.preventDefault(); }
    else if (e.key === 'Home') { seekRatio(0); e.preventDefault(); }
    else if (e.key === 'End') { seekRatio(1); e.preventDefault(); }
  }

  function wire() {
    if (els.toggle) els.toggle.addEventListener('click', toggle);
    if (els.close) els.close.addEventListener('click', closePlayer);
    if (els.seek) {
      els.seek.addEventListener('click', onSeekClick);
      els.seek.addEventListener('keydown', onSeekKey);
    }
    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest && e.target.closest('[data-preview-play]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation(); // keep the row's stretched link from firing
      onPreviewClick(btn);
    });
    // Persist position when leaving the page so the next page can resume.
    window.addEventListener('pagehide', function () { save(true); });
  }

  function init() {
    if (booted) return;
    booted = true;
    if (!cacheEls()) return;
    wire();
    resume();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
